/**
 * Dashboard widget catalog (@aiga/app-config/permissions): every widget an
 * admin can grant per role, as data. The one copy, read by both apps:
 *
 *   - the web registry (`src/widgets/registry.ts`) adds each key's icon and
 *     React component, and the matrix at /settings/widget-permissions lists it;
 *   - the API syncs it into `permissions` / `role_permissions` when it starts
 *     (`services/widget-permissions/sync-widget-permissions-at-boot.ts`), so a
 *     new widget's row and its default grants exist after the deploy, without
 *     an admin opening the settings page first.
 *
 * To add a widget: append it here, then give its key an icon and component in
 * the web registry's WIDGET_UI and a preview sample in the web's
 * `src/widgets/preview/sampleData/index.ts` (tsc fails until it has both).
 *
 * Moved from the web's `widgets/registry.ts` (finding 1 of the 2026-10-10
 * preview run: prod's last sync was 2026-08-10, so four newer widgets had no
 * permission row and Brokerage Assistants saw a blank dashboard).
 *
 * Persistence: a widget is a `permissions` row with perm_name
 * `WIDGET_PERM_PREFIX + key`; a role sees it when `role_permissions` links
 * them. Admin roles see every widget regardless (the web's useWidgetAccess).
 *
 * First-sync defaults
 *   - `defaultRoles` lists the role names granted the widget THE FIRST TIME
 *     its permission row is created. Later syncs (every boot, every visit to
 *     the matrix) do NOT re-apply them: an admin's revoke sticks across
 *     redeploys.
 *   - Use [] for "no role gets this by default" (admin must grant explicitly).
 *   - Spell roles with the role names and sets from ./roles (no quoted role
 *     names: the app-config tripwire fails on them).
 *
 * `key` is a stable identifier: never rename one (the rename orphans its
 * permission row and every grant on it).
 */
import { ADMIN_ROLE, ADMIN_ROLES, BROKER_ROLE, BROKERAGE_ASSISTANT_ROLE, HR_ASSISTANT_ROLE, HR_DIRECTOR_ROLE, MANAGER_PLUS_ROLES, MANAGER_ROLE, PROPERTY_ASSISTANT_ROLE, PROPERTY_MANAGER_ROLE, RESEARCH_ROLE, SUPER_ADMIN_ROLE, USER_ROLE, } from './roles.js';
export const WIDGET_PERM_PREFIX = 'widget:';
export const WIDGET_CATEGORIES = {
    default: 'Default dashboard',
    broker: 'Broker dashboard',
    brokerage_assistant: 'Brokerage Assistant dashboard',
    manager: 'Manager dashboard',
    shared: 'Shared widgets',
};
// Every authenticated non-broker role: the default-dashboard widgets' defaults.
const NON_BROKER_ROLES = [SUPER_ADMIN_ROLE, ADMIN_ROLE, MANAGER_ROLE, RESEARCH_ROLE, USER_ROLE];
const CATALOG = [
    // ─── Default dashboard ───────────────────────────────────────────
    {
        key: 'default_hero',
        label: 'Default hero panel',
        description: 'Greeting + role chip + up to three top-right action buttons, each shown only with its key: New Property (Properties: create), Ingest (Data Ingestion), Comps (Comp Intelligence).',
        category: 'default',
        defaultRoles: [...NON_BROKER_ROLES],
    },
    {
        key: 'default_kpi_strip',
        label: 'Default KPI strip',
        description: 'Headline KPI cards, each shown only with its module: Properties, Comps (Comp Intelligence), Comp Projects (Comp Reports), Ingestion Jobs (Data Ingestion). Hidden when the role has none of them.',
        category: 'default',
        defaultRoles: [...NON_BROKER_ROLES],
    },
    {
        key: 'recently_updated_properties',
        label: 'Recently Updated Properties feed',
        description: 'Ops Dashboard only: a scrollable 2-column list of the most-recently-edited properties across the catalog.',
        category: 'default',
        defaultRoles: [...NON_BROKER_ROLES],
        // Rendered only on the Ops Dashboard (web OpsDashboardPage), on no dashboard layout (2026-10-31 decision).
        dashboard: false,
    },
    {
        // Replaces the retired /comps/queue page — the queue now lives
        // as a dashboard-only surface for the research role. Managers get
        // it too so they can triage assignments from the same landing
        // page; Admin / Super Admin see it via the widget-access admin
        // short-circuit.
        key: 'research_queue',
        label: 'Research Queue',
        description: 'Oldest-first queue of incoming comp requests with filters and inline assignee picker. Intended for researchers.',
        category: 'default',
        defaultRoles: [RESEARCH_ROLE, MANAGER_ROLE],
    },
    {
        // Listings L1-4: the caller's listing tasks due today (overdue folded
        // in), linking into My work. Default dashboard layout — the Brokerage
        // Assistant's landing page. The widget also needs the Listings work
        // grant (feature:listings_work or an admin) and renders nothing
        // without it, so granting it to a role without Listings shows nothing.
        key: 'my_listing_tasks',
        label: 'My listing tasks',
        description: "The caller's open listing tasks due today, overdue ones included, high priority first; each opens the task in Listings → My work. Needs Listings work access too.",
        category: 'default',
        defaultRoles: [BROKERAGE_ASSISTANT_ROLE, ...MANAGER_PLUS_ROLES],
    },
    // ─── Shared widgets ──────────────────────────────────────────────
    {
        // F-B1 / P-04: personal pinned-properties list. Shared category —
        // pins are a per-user working set, so every role gets the widget
        // by default (the data is scoped to the caller regardless).
        key: 'pinned_properties',
        label: 'Pinned Properties',
        description: "The caller's pinned properties — card view by default with a table tab. Pins are personal bookmarks, unpinnable inline.",
        category: 'shared',
        defaultRoles: [...NON_BROKER_ROLES, BROKER_ROLE],
    },
    {
        // F-SM (2026-08-11): recipient-discovery for shared reports. A share
        // can target ANY role, so every authenticated role gets this widget
        // by default — the data is scoped to the caller's own active shares
        // regardless. Empty for a user with no shares (collapses cleanly).
        key: 'shared_with_me',
        label: 'Shared with me',
        description: 'Reports other users have shared with the caller, with a view-only badge when the share is read-only. Links straight to the report.',
        category: 'shared',
        defaultRoles: [...NON_BROKER_ROLES, BROKER_ROLE],
    },
    // ─── Broker dashboard ────────────────────────────────────────────
    {
        key: 'broker_hero',
        label: 'Broker hero panel',
        description: 'Greeting, role chip, intro line, and the two action buttons (Browse properties, Suggest a change).',
        category: 'broker',
        defaultRoles: [BROKER_ROLE],
    },
    {
        key: 'broker_metric_strip',
        label: 'Broker metric strip',
        description: 'Four KPI tiles: Assignments, In Pipeline, Pending Review, Decisions (30d).',
        category: 'broker',
        defaultRoles: [BROKER_ROLE],
    },
    {
        // Key stays 'my_assignments' (stable identifier — never rename);
        // display copy moved to "My Deals" per P-06 so the formal deal-role
        // list doesn't read like the personal pinned_properties watch list.
        key: 'my_assignments',
        label: 'My Deals card',
        description: 'List of properties formally assigned to the caller (deal role + side), with pipeline-stage rollup and per-row pipeline chip.',
        category: 'broker',
        defaultRoles: [BROKER_ROLE],
    },
    {
        key: 'my_submissions',
        label: 'My Submissions card',
        description: "The caller's data-update suggestions, with current review status and reviewer notes.",
        category: 'broker',
        defaultRoles: [BROKER_ROLE],
    },
    {
        key: 'my_requests',
        label: 'My Reports card',
        description: 'Every report the broker has created, plus everything research has released to them, with current status.',
        category: 'broker',
        defaultRoles: [BROKER_ROLE],
    },
    // ─── Brokerage Assistant dashboard ───────────────────────────────
    // Mounted only in the Brokerage Assistant layout (web dashboardLayouts.ts,
    // 2026-10-12 client feedback): an admin can grant them to another role, but
    // no other dashboard has a slot for them.
    {
        // Team workload on the dashboard: overdue and stuck listing tasks across
        // the Brokerage Assistants, linking into /listings/workload (M22b). The
        // API answers an admin, or a role holding feature:listings_work AND this
        // grant (data/listings-access.ts can_team_workload).
        key: 'team_workload',
        label: 'Team workload',
        description: 'Overdue and stuck listing tasks across the Brokerage Assistants, with a link to Listings → Team workload. Needs Listings work access too.',
        category: 'brokerage_assistant',
        defaultRoles: [BROKERAGE_ASSISTANT_ROLE, ...ADMIN_ROLES],
    },
    {
        // Listings L2-1, moved to the Brokerage Assistant (2026-10-12): the
        // listing projects the assistant runs — default assistant, or holding an
        // open task on it (GET /api/listings/agreements?assisting=) — each with
        // its latest activity line. Also needs the Listings module; hidden when
        // there are none.
        key: 'my_listings',
        label: 'My listings card',
        description: "The caller's active listing projects (default assistant, or an open task assigned to them), each with its latest activity line and a link to its activity. Needs Listings access too; hidden when there are none.",
        category: 'brokerage_assistant',
        defaultRoles: [BROKERAGE_ASSISTANT_ROLE],
    },
    {
        // Dashboard cards 3 (#4): the open "Ask the assistant" requests nobody has picked up (M74), linking to My work.
        // GET /api/listings/broker-requests takes feature:listings_work; the card renders nothing without it.
        key: 'broker_requests',
        label: 'Broker requests',
        description: 'Open requests brokers sent with "Ask the assistant" that nobody has picked up yet, newest first, with a link to Listings → My work to assign them. Needs Listings work access too.',
        category: 'brokerage_assistant',
        defaultRoles: [BROKERAGE_ASSISTANT_ROLE],
    },
    {
        // Dashboard cards 3 (#3): the broker reads its Active agreements (filtered on expiring_soon or the card's window),
        // the assistant reads my-tasks' expiring_agreements (a broker gets 403 there). Setting: windowDays.
        key: 'expiring_agreements',
        label: 'Expiring agreements',
        description: "The caller's active listing agreements nearing their expiration date, soonest first, within the card's window (default: the Listings warning window). A broker sees the agreements it is on; a Brokerage Assistant the ones it holds an open task on. Needs Listings access too.",
        category: 'shared',
        defaultRoles: [BROKER_ROLE, BROKERAGE_ASSISTANT_ROLE],
    },
    // ─── Broker dashboard (listings) ─────────────────────────────────
    {
        // Dashboard cards 3 (#2): the broker's active listing agreements, drawn as My listings draws them. Setting:
        // scope (me = the ones it leads, team = also the ones it is on the team of).
        key: 'my_listing_projects',
        label: 'My listing projects',
        description: "The caller's active listing agreements — the ones it leads, or also the ones it is on the team of (the card's scope) — each with its latest activity line and a link to its activity. Needs Listings access too.",
        category: 'broker',
        defaultRoles: [BROKER_ROLE],
    },
    // ─── Manager dashboard ───────────────────────────────────────────
    {
        // Dashboard cards 3 (#1): per Brokerage Assistant, the open listing tasks on schedule and overdue, plus the
        // unassigned ones. Reads Team workload, so it needs what that API asks: listings_work AND widget:team_workload.
        key: 'team_task_health',
        label: 'Team task health',
        description: 'Per Brokerage Assistant, the open listing tasks on schedule and overdue, plus the unassigned tasks, with a link to Listings → Team workload. Needs Listings work access and the Team workload card too.',
        category: 'manager',
        defaultRoles: [...ADMIN_ROLES],
    },
    {
        // Dashboard cards 4 (#5): GET /api/property-management/lease-expirations/summary (M42), the section's tab badge.
        // The card renders nothing without a feature:property_management key (any of the three; manager+ by default).
        key: 'lease_expirations',
        label: 'Lease expirations',
        description: 'Managed leases expiring in the next months (the Property management lease alerts window), then the count per year and the undated ones, with a link to Property management → Lease expirations. Needs Property management access too.',
        category: 'manager',
        defaultRoles: [...MANAGER_PLUS_ROLES],
    },
    // ─── Shared (dashboard cards 4) ──────────────────────────────────
    {
        // Dashboard cards 4 (#6): GET /api/forms/inbox?assignee=me&limit=1, its counts. No route gate: the API reads only
        // the types the caller processes (PROCESS goes to listings_work holders on the seeded type), so the default is
        // the Brokerage Assistant; the card shows to anyone with a form type (the sidebar's Forms rule).
        key: 'forms_inbox',
        label: 'Forms inbox',
        description: 'The open forms assigned to the caller, counted by status (Submitted, In progress, Returned), with the newest one and a link to the Forms inbox. Shows to anyone with a form type to submit or process.',
        category: 'shared',
        defaultRoles: [BROKERAGE_ASSISTANT_ROLE],
    },
    // ─── Broker dashboard (dashboard cards 4) ────────────────────────
    {
        // Dashboard cards 4 (#15), the smallest version: GET /api/me/assignments counted by pipeline stage, plus the
        // summed asking_price of the caller's ACTIVE listing agreements (with Listings access). Commission is free text,
        // never summed. No migration; M35 / M10 stay open.
        key: 'deal_pipeline',
        label: 'Pipeline (beta)',
        description: "The caller's deals counted by pipeline stage (Lead, Active, Closed, no stage), and the summed asking price on its active listing agreements (with Listings access). Beta: commission is not summed.",
        category: 'broker',
        defaultRoles: [BROKER_ROLE],
    },
    // ─── Manager dashboard (dashboard cards 5) ───────────────────────
    {
        // Dashboard cards 5 (#9): the review queues' own reads (never the generated /count): /api/reviews/suggestions
        // and /broker-submissions with limit=1 → total, /api/reviews/workflows → open. Each count also needs its queue's
        // key, as the API gates it: reviews (suggestions, workflows), comp_research_queue (broker submissions).
        key: 'review_queue_counts',
        label: 'Review queue counts',
        description: 'How much waits in each review queue: pending suggested changes, open review workflows, broker submissions not yet reviewed, each linking to its queue. Shows only the queues the role can open (Reviews; the research queue for broker submissions).',
        category: 'manager',
        defaultRoles: [...MANAGER_PLUS_ROLES],
    },
    {
        // Dashboard cards 5 (#10): the Ops dashboard's reads, /api/ops/top-actions?limit=5, /freshness and /sla. Needs
        // ops_dashboard too, which the API asks of /api/ops/*.
        key: 'data_quality',
        label: 'Data quality',
        description: 'Where to research next: the stale records among the 50 least recently touched, conflicts open past the SLA window, and the five most urgent Ops dashboard top actions, each linking to where it is worked. Needs the Ops dashboard too.',
        category: 'manager',
        defaultRoles: [...MANAGER_PLUS_ROLES],
    },
    // ─── Default dashboard (dashboard cards 6) ───────────────────────
    {
        // Dashboard cards 6 (#7): the dashboard summary's compRequestStats part. The Research / Admin tier counts every
        // request, anyone else its own (the comp-project visibility rule). The card also needs comp_requests.
        key: 'comp_request_turnaround',
        label: 'Comp request turnaround',
        description: 'Open comp requests that are unassigned, untouched or past their requested deadline, and the median time from submitted to first touched and to fulfilled. Research and admins see every request, anyone else their own. Needs Comp requests too.',
        category: 'default',
        defaultRoles: [RESEARCH_ROLE, ...ADMIN_ROLES],
    },
    {
        // Dashboard cards 6 (#11): the summary's ingestionReview part, behind feature:data_ingestion on the API too; the
        // admin roles also get the stuck-jobs line from GET /api/health/jobs. Default: the roles holding Data Ingestion.
        key: 'ingestion_review',
        label: 'Ingestion awaiting review',
        description: 'Ingestion jobs whose parsed records still wait on an Accept or Reject, with the longest waiting linking to their review; admins also see the stuck jobs. Needs Data Ingestion too.',
        category: 'default',
        defaultRoles: [...MANAGER_PLUS_ROLES, RESEARCH_ROLE],
    },
    // ─── Dashboard cards 7 ───────────────────────────────────────────
    {
        // Dashboard cards 7 (#12): GET /api/users/moments. Birthdays to anyone granted the card; new hires and
        // anniversaries (hire dates) only with feature:hr_protected_fields, which Admin / Super Admin do not pass.
        // Default: the HR roles; who else sees birthdays is the admin's grant.
        key: 'people_moments',
        label: 'People moments',
        description: 'Birthdays coming up and, for roles holding Protected HR fields, new hires and work anniversaries. Admins see the hire dates only with that key.',
        category: 'default',
        defaultRoles: [HR_DIRECTOR_ROLE, HR_ASSISTANT_ROLE],
    },
    {
        // Dashboard cards 7 (#13): GET /api/users/credential-expirations, behind feature:hr_directory on the API too.
        key: 'credential_expirations',
        label: 'License and notary expirations',
        description: 'Licenses and notary commissions that expire soon or already expired, soonest first, with whose they are. Needs the Directory too.',
        category: 'default',
        defaultRoles: [HR_DIRECTOR_ROLE, HR_ASSISTANT_ROLE],
    },
    {
        // Dashboard cards 7 (#14): GET /api/comps/rows per kind, newest first with createdFrom; the confidentiality rule
        // is in the SQL. Needs comp_intelligence, which the API asks of /api/comps/rows.
        key: 'new_comps',
        label: 'New comps this week',
        description: 'How many sale and lease comps were added this week (or the chosen window), and the newest few, each opening its transaction. Needs Comp Intelligence too.',
        category: 'shared',
        defaultRoles: [BROKER_ROLE, RESEARCH_ROLE, ...MANAGER_PLUS_ROLES],
    },
    // ─── Dashboard cards 8 ───────────────────────────────────────────
    {
        // Dashboard cards 8 (#16): the summary's announcements part, the ones shown today to everyone or to one of the
        // caller's roles. Admin / Super Admin also post and remove them in the card (the generated /api/announcements
        // CRUD, admin-only on the API). Default: every role named in ./roles.
        key: 'announcements',
        label: 'Announcements',
        description: 'Notices an admin posts for everyone or for chosen roles, pinned ones first, shown from their first to their last day. Admins post and remove them in the card.',
        category: 'shared',
        defaultRoles: [
            ...NON_BROKER_ROLES,
            BROKER_ROLE,
            BROKERAGE_ASSISTANT_ROLE,
            HR_DIRECTOR_ROLE,
            HR_ASSISTANT_ROLE,
            PROPERTY_MANAGER_ROLE,
            PROPERTY_ASSISTANT_ROLE,
        ],
    },
];
/** Every dashboard widget, in the matrix's order. */
export const WIDGET_CATALOG = CATALOG;
/** The dashboard cards, in the matrix's order: what the Dashboard designer places and its save grants or revokes. */
export const DASHBOARD_CARD_KEYS = CATALOG.filter((w) => !('dashboard' in w && w.dashboard === false)).map((w) => w.key);
const DASHBOARD_CARDS = new Set(DASHBOARD_CARD_KEYS);
/** True when `key` is a dashboard card (false for a widget shown on another page, or no catalog widget at all). */
export function isDashboardCard(key) {
    return DASHBOARD_CARDS.has(key);
}
/**
 * The KPI strip's counts (GET /api/dashboard/summary, part `counts`) and the module key each one
 * needs; null = shown to everyone who sees the strip. The API leaves out a count the caller has no
 * key for (Super Admin and AIG staff hold every key), and the web hides its card.
 */
export const KPI_COUNT_FEATURES = {
    properties: 'properties',
    saleTransactions: 'comp_intelligence',
    leaseTransactions: 'comp_intelligence',
    compProjects: 'comp_reports',
    ingestionJobs: 'data_ingestion',
};
/** The `perm_name` of a widget (`widgetPermName('team_workload')` → 'widget:team_workload'). */
export function widgetPermName(key) {
    return `${WIDGET_PERM_PREFIX}${key}`;
}
//# sourceMappingURL=widget-catalog.js.map